import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Rows3, LayoutList, StretchHorizontal } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Row density: 'compact' | 'comfortable' | 'spacious'
 *
 * Global setting persisted per user in localStorage. Consumers can:
 *   const { density, rowClass } = useRowDensity();
 *   <tr className={rowClass}>...</tr>
 *
 * Provides tailwind padding tokens so tables react uniformly. Ships
 * with a compact <RowDensityToggle /> segmented control for toolbars.
 */
const STORAGE_KEY = 'lodgiflow_row_density';
const RowDensityContext = createContext(null);

const CLASSES = {
  compact: 'py-1 text-xs',
  comfortable: 'py-2 text-sm',
  spacious: 'py-3.5 text-sm',
};

export function RowDensityProvider({ children, defaultDensity = 'comfortable' }) {
  const [density, setDensityState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v && CLASSES[v]) return v;
    } catch {}
    return defaultDensity;
  });

  const setDensity = useCallback((next) => {
    if (!CLASSES[next]) return;
    setDensityState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  useEffect(() => {
    // expose as data-attr on <html> so global CSS can also react
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const value = { density, setDensity, rowClass: CLASSES[density] };
  return <RowDensityContext.Provider value={value}>{children}</RowDensityContext.Provider>;
}

export function useRowDensity() {
  const ctx = useContext(RowDensityContext);
  if (!ctx) {
    // safe fallback for components rendered outside provider
    return { density: 'comfortable', setDensity: () => {}, rowClass: CLASSES.comfortable };
  }
  return ctx;
}

const OPTIONS = [
  { id: 'compact', label: 'Compact', icon: StretchHorizontal },
  { id: 'comfortable', label: 'Comfy', icon: LayoutList },
  { id: 'spacious', label: 'Spacious', icon: Rows3 },
];

export default function RowDensityToggle({ className = '' }) {
  const { density, setDensity } = useRowDensity();
  return (
    <div
      className={`inline-flex items-center rounded-md border bg-card p-0.5 ${className}`}
      data-testid="row-density-toggle"
      role="radiogroup"
      aria-label="Row density"
    >
      {OPTIONS.map(o => {
        const active = density === o.id;
        const Icon = o.icon;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setDensity(o.id)}
            title={o.label}
            className={`inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] transition-colors ${active ? 'bg-muted text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`row-density-${o.id}`}
          >
            <Icon className="h-3 w-3" />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
